import { createContext, useContext, useEffect, useState } from "react";
import { supabase, isSupabaseConfigured } from "../services/supabase";
import { SELECT_COACH_ROW } from "../constants/supabaseSelectLists";
import useAppStore from "../stores/useAppStore";
import { logger } from "../utils/logger";

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [coach, setCoach] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured() || !supabase) {
      setLoading(false);
      return undefined;
    }

    const handleAuth = async (session) => {
      const nextUser = session?.user ?? null;
      setUser(nextUser);
      if (nextUser) {
        const { data } = await supabase.from("coaches").select(SELECT_COACH_ROW).eq("id", nextUser.id).single();
        if (data) {
          setCoach(data);
          await supabase
            .from("coaches")
            .update({ last_login_at: new Date().toISOString() })
            .eq("id", nextUser.id);
        } else {
          setCoach(null);
        }
        await useAppStore.getState().hydrate(nextUser.id);
      } else {
        setCoach(null);
        useAppStore.setState({ loggedIn: false, coachId: "local-coach", booting: false });
      }
      setLoading(false);
    };

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      void handleAuth(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  async function signInWithGoogle() {
    if (!supabase) return;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/auth/callback` },
    });
    if (error) throw error;
  }

  async function signInWithEmail(email, password) {
    if (!supabase) return;
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  }

  async function signUpWithEmail(email, password, meta = {}) {
    if (!supabase) return;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          first_name: meta.firstName,
          last_name: meta.lastName,
        },
      },
    });
    if (error) throw error;
    const uid = data?.user?.id;
    if (uid && (meta.firstName || meta.lastName)) {
      const { error: upErr } = await supabase
        .from("coaches")
        .update({
          first_name: meta.firstName?.trim() || null,
          last_name: meta.lastName?.trim() || null,
        })
        .eq("id", uid);
      if (upErr) logger.warn("[auth] coach name update", { detail: upErr.message });
    }
  }

  async function signOut() {
    if (supabase) await supabase.auth.signOut();
    setUser(null);
    setCoach(null);
    window.location.assign("/login");
  }

  const isAdmin = coach?.role === "admin";

  return (
    <AuthContext.Provider
      value={{
        user,
        coach,
        loading,
        isAdmin,
        signInWithGoogle,
        signInWithEmail,
        signUpWithEmail,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
